import React, { useState } from 'react';
import { Mail, Eye, EyeOff } from 'lucide-react';
import Button from '../components/Button';
import Input from '../components/Input';
import authService from '../services/authService';

interface LoginFormData {
  email: string;
  password: string;
}

const SignIn: React.FC = () => {
  const [formData, setFormData] = useState<LoginFormData>({
    email: '',
    password: ''
  });

  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');

    try {
      const response = await authService.signin({
        email: formData.email,
        password: formData.password
      });

      if (response.success) {
        document.cookie = `sgo_access_token=${response.data.access_token}; path=/`;
        const params = new URLSearchParams(window.location.search);
        const redirect = params.get('redirect');
        window.location.href = redirect || '/feed';
      } else {
        throw new Error(response.message || 'Erro ao fazer login');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Email ou senha inválidos. Tente novamente.');
      console.error('Login error:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-cream-50 via-cream-100 to-gold-100 flex items-center justify-center px-4 py-8">
      <div className="max-w-md w-full">
        {/* Logo/Header */}
        <div className="text-center mb-8">
          <div className="flex justify-center mb-4">
            <img
              src="/santogram_logo.png"
              alt="SantoGram"
              className="h-24 w-auto"
            />
          </div>
          <p className="text-brown-600">
            Entre para acessar sua jornada espiritual
          </p>
        </div>

        {/* Login Form */}
        <div className="bg-white rounded-2xl p-8 shadow-xl border border-cream-200">
          <div className="mb-6 text-center">
            <h2 className="text-2xl font-display font-bold text-brown-800 mb-2">
              Bem-vindo de volta
            </h2>
            <p className="text-brown-600 text-sm">
              Faça login para continuar sua jornada espiritual
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Email Field */}
            <div className="relative">
              <Input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                placeholder="seu@email.com"
                label="E-mail"
                required
              />
              <Mail className="absolute right-3 top-[52px] transform -translate-y-1/2 w-5 h-5 text-brown-400 pointer-events-none" />
            </div>

            {/* Password Field */}
            <div className="relative">
              <Input
                type={showPassword ? 'text' : 'password'}
                id="password"
                name="password"
                value={formData.password}
                onChange={handleInputChange}
                placeholder="Sua senha"
                label="Senha"
                required
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-[52px] transform -translate-y-1/2 text-brown-400 hover:text-brown-600"
              >
                {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
              </button>
            </div>

            {/* Remember me & Forgot password */}
            <div className="flex items-center justify-between">
              <label className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  className="w-4 h-4 text-gold-600 border-cream-300 rounded focus:ring-gold-500"
                />
                <span className="text-sm text-brown-700">Lembrar de mim</span>
              </label>
              <span className="cursor-pointer text-sm text-gold-600 hover:text-gold-700 font-medium">
                Esqueceu a senha?
              </span>
            </div>

            {/* Error Message */}
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <p className="text-red-700 text-sm">{error}</p>
              </div>
            )}

            {/* Submit Button */}
            <Button
              type="submit"
              disabled={isSubmitting}
              className="w-full"
            >
              {isSubmitting ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  Entrando...
                </>
              ) : (
                'Entrar'
              )}
            </Button>
          </form>

          {/* Divider */}
          <div className="mt-8 mb-6">
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-cream-300"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 bg-white text-brown-500">ou</span>
              </div>
            </div>
          </div>

          {/* Sign up link */}
          <div className="text-center">
            <p className="text-sm text-brown-600">
              Ainda não tem uma conta?{' '}
              <a href="#inscrever" className="text-gold-600 hover:text-gold-700 font-medium">
                Inscreva-se gratuitamente
              </a>
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="mt-8 text-center">
          <p className="text-xs text-brown-500">
            Ao fazer login, você concorda com nossos{' '}
            <span className="cursor-pointer text-gold-600 hover:text-gold-700">
              Termos de Uso
            </span>{' '}
            e{' '}
            <span className="cursor-pointer text-gold-600 hover:text-gold-700">
              Política de Privacidade
            </span>
          </p>
        </div>
      </div>
    </div>
  );
};

export default SignIn;